/* eslint-disable no-underscore-dangle */
import {
  get, post, put, del,
} from './API';
import toNumber from '../Utils/toNumber';

const mapArray = (arr, fn) => (Array.isArray(arr) ? arr.map(fn) : arr);

const serializeBookingForApi = (order) => ({
  ...order,
  original_price: toNumber(order.original_price),
  price: toNumber(order.price),
  operator_price: toNumber(order.operator_price),
  operator_cash_from_client: toNumber(order.operator_cash_from_client),
  operator_cancellation_price: toNumber(order.operator_cancellation_price),
  partner_cancellation_price: toNumber(order.partner_cancellation_price),
  passengers: order.passengers && {
    ...order.passengers,
    adults: toNumber(order.passengers.adults),
    amount: toNumber(order.passengers.amount),
    convertible_seat: toNumber(order.passengers.convertible_seat),
    booster_seat: toNumber(order.passengers.booster_seat),
    infant_carrier: toNumber(order.passengers.infant_carrier),
    children: toNumber(order.passengers.children),
  },
  destination_from: order.destination_from && {
    ...order.destination_from,
    latitude: toNumber(order.destination_from.latitude),
    longitude: toNumber(order.destination_from.longitude),
  },
  destination_to: order.destination_to && {
    ...order.destination_to,
    latitude: toNumber(order.destination_to.latitude),
    longitude: toNumber(order.destination_to.longitude),
  },
  operators: mapArray(order.operators, (op) => ({
    ...op,
    price: toNumber(op.price),
    cash_from_client: toNumber(op.cash_from_client),
  })),
  programme: order.programme && {
    ...order.programme,
    total_hours: toNumber(order.programme.total_hours),
    stops: mapArray(order.programme.stops, (stop) => ({
      ...stop,
      order: toNumber(stop.order),
      duration_minutes: toNumber(stop.duration_minutes),
      latitude: toNumber(stop.latitude),
      longitude: toNumber(stop.longitude),
    })),
  },
  order_lines: mapArray(order.order_lines, (line) => ({
    ...line,
    credit: toNumber(line.credit),
    credit_operator: toNumber(line.credit_operator),
    debit: toNumber(line.debit),
  })),
  discount: order.discount && {
    ...order.discount,
    percentage: toNumber(order.discount.percentage),
  },
  category: order.category && {
    ...order.category,
    capacity: toNumber(order.category.capacity),
    max_luggage: toNumber(order.category.max_luggage),
  },
});

export default {
  getBookingById: async (id) => {
    const response = await get('/bookings', { filters: { _id: id } });
    const documents = response.data?.data?.documents || [];
    return documents.length > 0 ? documents[0] : null;
  },
  // One booking by its reference, for a screen that has the reference and not
  // the id — the customer-service viewer reads it out of a mail subject.
  //
  // NESTED `filters[booking_reference]`, NOT the flat `?booking_reference=`:
  // the handler reserves the top-level key for its `action=stripe-billing`
  // mode and drops it from the filter set, so the flat form would quietly
  // return the first page of ALL bookings.
  //
  // Defensive exact re-check, like listBookingsForOffer below: the server
  // matches a reference as a case-insensitive REGEX, so a reference that is a
  // prefix of a longer one (PU_DASH_123 against PU_DASH_1234567) comes back
  // too. Returning somebody else's booking against a mail subject is the one
  // failure this must not have.
  getBookingByReference: async (reference) => {
    if (!reference) return null;
    const response = await get('/bookings', { filters: { booking_reference: reference } });
    const documents = response.data?.data?.documents || [];
    const wanted = String(reference).toUpperCase();
    return documents.find((d) => String(d?.booking_reference).toUpperCase() === wanted) || null;
  },
  // Fetch every booking ingested from a given service offer. Powers
  // the IngestWorkflow's load-time reconciliation: when the operator
  // reopens an in-flight ingest page, rows that already landed are
  // surfaced as "already ingested" instead of being re-proposed.
  //
  // Defensive client-side filter: the server-side handler must apply
  // the `source_offer_id` filter, but if it doesn't (handler not yet
  // deployed, unknown filter silently dropped, etc.) we'd otherwise
  // mis-report the first 200 of *all* bookings as "already ingested
  // from this offer". Re-check on the client so a backend regression
  // can never inflate the count.
  listBookingsForOffer: async (offerId) => {
    if (!offerId) return [];
    const response = await get('/bookings', {
      filters: { source_offer_id: offerId },
      pageSize: 200,
    });
    const documents = response.data?.data?.documents || [];
    return documents.filter((d) => d && d.source_offer_id === offerId);
  },
  postBooking: async (order) => {
    /* eslint-disable no-param-reassign */
    if (order.booking_type === 'experience') {
      // For experiences: adults + children (no seat requirements)
      order.passengers.amount = Number(order.passengers.adults)
        + Number(order.passengers.children || 0);
    } else {
      // For transfers: adults + seat counts
      order.passengers.amount = Number(order.passengers.adults || 0)
        + Number(order.passengers.infant_carrier || 0)
        + Number(order.passengers.booster_seat || 0)
        + Number(order.passengers.convertible_seat || 0);
    }
    /* eslint-enable no-param-reassign */

    const response = await post('/bookings', serializeBookingForApi(order));
    return response.data?.data;
  },
  assignDriverAndCar: async (order) => {
    await post('/bookings/assign-driver-and-car', { ...order });
  },
  addInvoice: async (order) => {
    await post('/bookings/invoices', { ...order });
  },
  getDriverEvents: async (order) => get('/bookings/driver-events', { _id: order._id }),

  // Booking change audit log
  getBookingChanges: async (bookingId, { limit, startKey } = {}) => {
    const params = { _id: bookingId };
    if (limit !== undefined) params.limit = limit;
    if (startKey !== undefined && startKey !== null) {
      params.startKey = encodeURIComponent(JSON.stringify(startKey));
    }
    const response = await get('/bookings/booking-changes', params);
    return response.data?.data || {};
  },

  // Event booking methods
  getEventNames: async () => {
    const response = await get('/bookings/events');
    return response.data?.data?.events || [];
  },
  getEventBookings: async (eventName) => {
    const response = await get(`/bookings/events/${encodeURIComponent(eventName)}`);
    return response.data?.data || { eventName, bookings: [] };
  },
  searchEventPassengers: async (eventName, searchTerm) => {
    const response = await get(`/bookings/events/${encodeURIComponent(eventName)}/search`, { q: searchTerm });
    return response.data?.data?.results || [];
  },
  getEventGroupSuggestions: async (eventName) => {
    const response = await get(`/bookings/events/${encodeURIComponent(eventName)}/suggestions`);
    return response.data?.data?.suggestions || [];
  },
  bulkUpdateEventGroup: async (bookingIds, eventGroup) => {
    const response = await post('/bookings/events/bulk-update-group', { bookingIds, eventGroup });
    return response.data?.data;
  },
  // assignments: [{ booking_id, operator_id, operator_price?, operator_currency? }]
  bulkAssignOperator: async (assignments) => {
    const response = await post('/bookings/events/bulk-assign-operator', { assignments });
    return response.data?.data;
  },
  // assignments: [{ booking_id, operator_driver_id, operator_car_id }]
  bulkAssignDriver: async (assignments) => {
    const response = await post('/bookings/events/bulk-assign-driver', { assignments });
    return response.data?.data;
  },
  // Update specific fields of a booking (for linked bookings sync)
  // Uses PUT for partial update without full validation
  updateBookingFields: async (bookingId, fields) => {
    await put('/bookings', { _id: bookingId, ...fields });
  },

  // Billing address Stripe captured for this booking's card payment.
  // Returns null when no payment intent carries this booking reference —
  // charges reconciled after the fact have no reachable intent row.
  fetchStripeBilling: async (bookingReference) => {
    const response = await get('/bookings', {
      action: 'stripe-billing',
      booking_reference: bookingReference,
    });
    return response.data?.data ?? null;
  },

  // Booking reminder methods
  addBookingReminder: async (bookingId, data) => {
    const response = await post('/bookings/notifications', {
      bookingId,
      ...data,
    });
    return response.data?.data;
  },
  updateBookingReminder: async (bookingId, reminderId, fields) => {
    const response = await post('/bookings/notifications', {
      bookingId,
      reminderId,
      action: 'update',
      ...fields,
    });
    return response.data?.data;
  },
  completeBookingReminder: async (bookingId, reminderId) => {
    const response = await post('/bookings/notifications', { bookingId, reminderId, action: 'complete' });
    return response.data?.data;
  },
  deleteBookingReminder: async (bookingId, reminderId) => {
    const response = await del('/bookings/notifications', { bookingId, reminderId });
    return response.data?.data;
  },
};
